import React, { useCallback, useEffect, useState } from "react";
import ResponsiveImage from "./ResponsiveImage";
import { IconChevronLeft, IconChevronRight, IconClose } from "./Icons";

const Lightbox = ({ items, index, onClose, onNavigate }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNavigate((index + 1) % items.length);
      if (e.key === "ArrowLeft") onNavigate((index - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [index, items.length, onClose, onNavigate]);

  const item = items[index];
  if (!item) return null;

  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={item.title}>
      <div className="lightbox__bar">
        <span>
          {index + 1} / {items.length}
        </span>
        <button type="button" className="lightbox__close" onClick={onClose} aria-label="Close">
          <IconClose />
        </button>
      </div>

      <div className="lightbox__stage">
        <button
          type="button"
          className="lightbox__nav lightbox__nav--prev"
          onClick={() => onNavigate((index - 1 + items.length) % items.length)}
          aria-label="Previous photo"
        >
          <IconChevronLeft />
        </button>

        <ResponsiveImage
          src={item.image}
          alt={item.title}
          sizes="(max-width: 900px) 100vw, 1100px"
          loading="eager"
          fetchPriority="high"
        />

        <button
          type="button"
          className="lightbox__nav lightbox__nav--next"
          onClick={() => onNavigate((index + 1) % items.length)}
          aria-label="Next photo"
        >
          <IconChevronRight />
        </button>
      </div>

      <div className="lightbox__foot">{item.title}</div>
    </div>
  );
};

const FILTERS = [
  { id: "All", match: () => true },
  { id: "Living", match: (t) => /living|relax/i.test(t) },
  { id: "Kitchen", match: (t) => /kitchen/i.test(t) },
  { id: "Bedrooms", match: (t) => /room|shower|bathroom/i.test(t) },
  { id: "Terrace", match: (t) => /terrace|balcony/i.test(t) },
  { id: "Entrance", match: (t) => /entrance/i.test(t) },
];

export const Gallery = ({ data }) => {
  const images = data || [];
  const [filter, setFilter] = useState("All");
  const [visible, setVisible] = useState(6);
  const [lightbox, setLightbox] = useState(null);

  const active = FILTERS.find((f) => f.id === filter) || FILTERS[0];
  const filtered = images.filter((i) => active.match(i.title));
  const shown = filtered.slice(0, visible);

  const openAt = useCallback((i) => setLightbox(i), []);
  const close = useCallback(() => setLightbox(null), []);
  const navigate = useCallback((i) => setLightbox(i), []);

  useEffect(() => {
    setVisible(6);
  }, [filter]);

  return (
    <section className="section" id="gallery">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">The house</span>
          <h2>Gallery</h2>
          <hr className="rule" />
          <p>
            Spacious rooms named after the family, a fully equipped kitchen and a terrace made for
            Positano sunsets.
          </p>
        </div>

        <div className="gallery__filters" role="group" aria-label="Filter photos">
          {FILTERS.map((c) => (
            <button
              key={c.id}
              type="button"
              className="chip"
              aria-pressed={filter === c.id}
              onClick={() => setFilter(c.id)}
            >
              {c.id}
            </button>
          ))}
        </div>

        <div className="gallery__grid">
          {shown.map((item, i) => (
            <button
              key={`${item.image}-${i}`}
              type="button"
              className="gallery__item"
              onClick={() => openAt(i)}
              aria-label={`Open photo: ${item.title}`}
            >
              <ResponsiveImage
                src={item.image}
                alt={item.title}
                sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 380px"
              />
              <span className="gallery__caption">{item.title}</span>
            </button>
          ))}
        </div>

        {visible < filtered.length && (
          <div className="gallery__more">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setVisible((v) => v + 6)}
            >
              Show more photos
            </button>
          </div>
        )}
      </div>

      {lightbox !== null && (
        <Lightbox items={shown} index={lightbox} onClose={close} onNavigate={navigate} />
      )}
    </section>
  );
};

export default Gallery;
